/* @flow */

import type { BasicFunction, BasicPromiseFunction } from '../../helpers/jsHelpers/types';
import type { ParentalControlItemState } from '../../helpers/parentalControl/constsAndTypes';
import type { Settings, SettingValue } from '../../helpers/settings/types';
import type { EXTENDED_ITEM } from '../../helpers/ui/item/types';
import type { STREAM_PRIORITIES_TYPE } from '../../helpers/ui/metadata/Types';
import type { LuminosityType } from '../../helpers/ui/theme';
import type { DmsSettingMap } from '../../libs/netgemLibrary/dms/types/DeviceInfoSettings';
import type { NETGEM_API_ENTITLEMENT_RESULT } from '../../libs/netgemLibrary/ntgEntitlement/types';
import type { ChannelMap, NETGEM_API_CHANNEL } from '../../libs/netgemLibrary/v8/types/Channel';
import type { DATA_COLLECTION_INTERNAL_MESSAGE } from '../../libs/netgemLibrary/v8/types/DataCollection';
import type { NETGEM_API_V8_FEED_ITEM } from '../../libs/netgemLibrary/v8/types/FeedItem';
import type { MetadataKind, NETGEM_API_V8_METADATA, NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../libs/netgemLibrary/v8/types/MetadataProgram';
import type {
  LOCATION_METADATA_METRICS,
  NETGEM_API_V8_METADATA_SCHEDULE_LOCATION,
  NETGEM_API_V8_METADATA_SCHEDULE_VIDEO_STREAM_ENTITLEMENT_PARAM,
  NETGEM_API_V8_METADATA_SCHEDULE_VIDEO_STREAM_PROPERTIES_PARAM,
} from '../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_RECORDINGS_MAP } from '../../libs/netgemLibrary/v8/types/Npvr';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../libs/netgemLibrary/v8/types/RequestResponse';
import type { NETGEM_API_VIEWINGHISTORY, NETGEM_API_VIEWINGHISTORY_ITEM } from '../../libs/netgemLibrary/v8/types/ViewingHistory';
import type { BO_PURCHASE_LIST_TYPE } from '../../redux/netgemApi/actions/videofutur/types/purchase';
import type { BO_API_CREATE_STREAM_TYPE } from '../../redux/netgemApi/actions/videofutur/types/stream';
import type { CombinedReducers } from '../../redux/reducers';
import type { CONFIRMATION_DATA_MODAL_TYPE } from '../modal/confirmationModal/ConfirmationModal';
import type { MESSAGE_MODAL_TYPE } from '../modal/messageModal/MessageModal';
import type { PIN_DATA_MODAL_TYPE } from '../modal/pinModal/PinModalConstsAndTypes';
import type { ShakaOfflineContent } from './implementation/shakaTypes';
import type { VideoPlayerDebugInfo, VideoPlayerExternalSubtitles, VideoPlayerInitData, VideoPlayerMediaInfo } from './implementation/types';

import { Drm } from '../../helpers/jsHelpers/Drm';
import { LoadableStatus } from '../../helpers/loadable/loadable';
import { Setting } from '../../helpers/settings/types';
import { WebAppHelpersLocationStatus } from '../../helpers/ui/location/Format';
import { ControlLevel } from '../../helpers/ui/player';
import ContinuousWatching from '../../helpers/viewingHistory/ContinuousWatching';
import { StreamType } from '../../libs/netgemLibrary/v8/types/MetadataSchedule';

export enum StopStreamKind {
  FinalStop,
  Initialization,
  Pause,
}

export enum SkippingKind {
  BackwardLevel0,
  BackwardLevel1,
  BackwardLevel2,
  ForwardLevel0,
  ForwardLevel1,
  ForwardLevel2,
  None,
}

export type NTGENTITLEMENT_PARAMS = {
  channelId: ?string,
  customData: string,
  service: string,
  url: string,
};

/*
 * Steps:
 *   NotStarted > LoadingProgramMetadata > Loaded | ItemNotFound
 * or:
 *   NotStarted > LoadingProgramMetadata > LoadingSeriesMetadata > Loaded | ItemNotFound
 */
export enum ProgramInfoLoadStatus {
  NotStarted,
  LoadingProgramMetadata,
  LoadingSeriesMetadata,
  Loaded,
  ItemNotFound,
}

/*
 * Resume mechanism can be in 4 states:
 *  - NotChecked: we've not checked yet if a resume position exists
 *  - NoResumePosition: no resume position has been found -> nothing to do
 *  - PromptUserIfNeeded: a resume positions exists, user should be prompted to use it or not (skipped if "auto resume" setting is enabled)
 *  - Seeking: user chose to restart or resume (could take a little time since we have to wait that the video actually starts playing)
 *  - Done: user made a choice (resume or restart) and video seeked to the right position
 */
export enum ResumeState {
  NotChecked,
  NoResumePosition,
  PromptUserIfNeeded,
  Seeking,
  Done,
}

export enum ContentType {
  // Live TV program
  Live,
  // Live TV program being recorded (including end margin)
  LiveRecording,
  // Catchup, finished recording, VOD
  Static,
}

export const DOUBLE_CLICK = Object.freeze({
  // In percent
  BackwardSkipAreaThreshold: 0.2,
  // In percent
  ForwardSkipAreaThreshold: 0.8,
  // In milliseconds
  MaxDuration: 300,
});

// Display duration of skip and play/pause pictos (in milliseconds)
export const BLINKING_PICTO_DURATION = 500;

// Display duration of controls (in milliseconds)
export const OVERLAY_TIMEOUT = 3_000;

// Time between two updates of the player debug info metrics (in milliseconds)
export const DEBUG_INFO_METRICS_TIMEOUT = 100;

// In seconds
export const PLAYHEAD_POSITION_CHANGE_THRESHOLD = 5;

// In milliseconds
export const CLOSE_ATTEMPT_WHILE_UPDATING_VIEWING_HISTORY_TIMEOUT = 200;

// Increment/decrement when changing volume through keyboard shortcut
export const VOLUME_STEP = 0.05;

// Playback rate is capped at 16 in most browsers
export const PLAYBACK_RATES: Array<number> = Object.freeze([0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2, 5, 10, 16]); // eslint-disable-line no-magic-numbers
export const NORMAL_PLAYBACK_INDEX: number = PLAYBACK_RATES.findIndex((r) => r === 1);

export const ONE_HUNDRED = 100;
export const ONE_THOUSAND = 1_000;

// Playback duration greater than twice the program duration is considered an error
export const PLAYBACK_DURATION_ERROR_THRESHOLD = 2;

// If position is between <now - TIMESHIFT_THRESHOLD> and <now>, we consider the stream is played live, otherwise, we're timeshifting (in seconds)
export const TIMESHIFT_THRESHOLD = 5;

// Code used in 'handleClose' function to indicate that the exit is not due to an error
export const NO_ERROR_CODE = 'no_error';

// Image dimension for Global Media Controls
export const GMC_IMAGE_WIDTH = 320;
export const GMC_IMAGE_HEIGHT = 184;

// Image dimension for cover in controller
export const COVER_IMAGE_WIDTH = 108;
export const COVER_IMAGE_HEIGHT = 160;

// Video resolution
export const RESOLUTION = Object.freeze({
  FourK: {
    Height: 2160,
    Width: 3840,
  },
  HD: {
    Height: 720,
    Width: 1280,
  },
});

export type ErrorCallback = BasicFunction | BasicPromiseFunction;

export type ViewingHistoryIds = {
  isVod: boolean,
  locationId: string | null,
  programId: string,
  seriesId: string | null,
};

export type ResumeData = {
  resumePosition: number,
  resumeState: ResumeState,
};

export type CloseData = {
  downloadOperationId?: string | null,
  errorCode?: string | null,
  errorMsg?: string | null,
  isLargeNotificationNeeded?: boolean,
};

export type PlayableStream = {
  drm?: Drm,
  laUrl?: string | null,
  ntgEntitlement?: NETGEM_API_V8_METADATA_SCHEDULE_VIDEO_STREAM_ENTITLEMENT_PARAM,
  path: string,
  properties?: NETGEM_API_V8_METADATA_SCHEDULE_VIDEO_STREAM_PROPERTIES_PARAM,
  type: StreamType,
};

export type CreateVodStreamFunction = (distributorId: string, vtiId: number, signal?: AbortSignal) => Promise<BO_API_CREATE_STREAM_TYPE>;

export type ReduxPlayerDispatchToPropsType = {
  +closeConfirmation: BasicFunction,
  +localGetImageUrl: (assetId: string, width: number, height: number, luminosity?: LuminosityType, signal?: AbortSignal) => Promise<string>,
  +localCreateSvodStreamFromId: CreateVodStreamFunction,
  +localCreateVodStreamFromId: CreateVodStreamFunction,
  +localSendNtgEntitlementGetRequest: (customData: string, service: string, url: string, channelId: string, signal?: AbortSignal) => Promise<NETGEM_API_ENTITLEMENT_RESULT>,
  +localSendNtgEntitlementReleaseRequest: (customData: string, service: string, url: string, channelId: string, signal?: AbortSignal) => Promise<void>,
  +localSendV8MetadataLocationRequest: (assetId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>,
  +localSendV8MetadataRequest: (assetId: string, type: MetadataKind, signal?: AbortSignal) => Promise<NETGEM_API_V8_METADATA>,
  +localSendV8RecordingsMetadataRequest: (recordId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>,
  +localSendVideofuturStreamCreateRequest: (distributorId: string, vtiId: number, licence: boolean, signal?: AbortSignal) => Promise<BO_API_CREATE_STREAM_TYPE>,
  +localSendVideofuturStreamStartRequest: (distributorId: string, streamId: number, signal?: AbortSignal) => Promise<void>,
  +localSendVideofuturStreamStopRequest: (distributorId: string, streamId: number, bookmark?: string, signal?: AbortSignal, keepAlive?: boolean) => Promise<void>,
  +localShowConfirmation: (data: CONFIRMATION_DATA_MODAL_TYPE, ariaLabel: string) => void,
  +localShowMessageModal: (messageData: MESSAGE_MODAL_TYPE) => void,
  +localShowPinModal: (data: PIN_DATA_MODAL_TYPE) => void,
  +localShowPresenceCheckModal: (isLive: boolean) => void,
  +localStopOpenStreams: (distributorId: string, signal?: AbortSignal) => Promise<number>,
  +localUpdateSetting: (setting?: Setting, value: SettingValue) => void,
  +localUpdateViewingHistory: (item: NETGEM_API_VIEWINGHISTORY_ITEM, signal?: AbortSignal) => Promise<void>,
};

export type ReduxPlayerReducerStateType = {
  +applicationName: string,
  +authenticationToken: string | null,
  +channels: ChannelMap,
  +deviceSettings: DmsSettingMap,
  +isDebugModeEnabled: boolean,
  +isMaxBitrateAllowed: boolean,
  +maxBitrate: number,
  +npvrRecordingsList: NETGEM_RECORDINGS_MAP,
  +purchaseList: BO_PURCHASE_LIST_TYPE,
  +settings: Settings,
  +state: CombinedReducers,
  +streamPriorities: STREAM_PRIORITIES_TYPE | null,
  +viewingHistory: NETGEM_API_VIEWINGHISTORY,
  +viewingHistoryStatus: LoadableStatus,
};

export type PlayerPropType = {
  +closeCallback: BasicFunction,
  +initialPlayerItem: EXTENDED_ITEM | null,
  +playerOfflineContent: ShakaOfflineContent | null,
};

export type CompletePlayerPropType = {
  ...PlayerPropType,
  ...ReduxPlayerDispatchToPropsType,
  ...ReduxPlayerReducerStateType,
};

export type PlayerStateInitializationType = {
  endMargin: number,
  externalSubtitles: Array<VideoPlayerExternalSubtitles>,
  isBuffering: boolean,
  isExiting: boolean,
  isVideofuturAsset: boolean,
  locationId: string | null,
  programInfoLoadStatus: ProgramInfoLoadStatus,
  programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  realEnd: number,
  realStart: number,
  seriesEpisodeText: string | null,
  seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
  startMargin: number,
  title: string | null,
  videoStreamData: VideoPlayerInitData | null,
  viewingHistoryId: string | null,
  vtiId: number | null,
};

export type PlayerStateType = {
  ...PlayerStateInitializationType,
  audioMediaInfo: Array<VideoPlayerMediaInfo>,
  bufferedTimeRanges: TimeRanges | null,
  channel: NETGEM_API_CHANNEL | null,
  channelImageUrl: string,
  contentType: ContentType,
  continuousWatching: ContinuousWatching | null,
  controlLevel: ControlLevel,
  currentItem: NETGEM_API_V8_FEED_ITEM | null,
  // Last time playbackDuration was updated (0 when paused) (in seconds)
  dataCollectionLastPlaybackTime: number,
  // Current data collection player state
  dataCollectionMessage: DATA_COLLECTION_INTERNAL_MESSAGE | null,
  // Time of the cdata collection session start
  dataCollectionStartTime: number,
  debugInfo: VideoPlayerDebugInfo,
  // Either the Id of the download operation or null if none is in progress
  downloadOperationId: string | null,
  // Initialized first with the duration from the program's metadata, then adjusted by the player
  duration: number,
  // Used to restart session without losing playhead position (Broadpeak keep-alive workaround)
  forcedResumePosition: number | null,
  imageUrl: string | null,
  isBOStreamRequestPending: boolean,
  isControllerEnabled: boolean,
  isCurrentItemBlackedOut: boolean,
  isDebugOverlayVisible: boolean,
  isInFullscreen: boolean,
  // Following property is used to display a recording picto while playing a live stream (when contentType === ContentType.Live)
  isLiveRecording: boolean,
  isZapping: boolean,
  isMuted: boolean,
  isOfflineContent: boolean,
  isOverlayVisible: boolean,
  isPlaying: boolean,
  isTimeshiftEnabled: boolean,
  isTimeshiftSwitching: boolean,
  isTrailer: boolean,
  isUnlockedAdultContent: boolean,
  isVideoQualityAuto: boolean,
  liveBufferLength: number,
  location: NETGEM_API_V8_METADATA_SCHEDULE_LOCATION | null,
  locationMetrics: ?Array<LOCATION_METADATA_METRICS>,
  locationStatus: WebAppHelpersLocationStatus | null,
  parentalControlState: ParentalControlItemState,
  playheadPosition: number,
  resumePosition: number,
  resumeState: ResumeState,
  selectedAudioMediaInfo: number,
  selectedSubtitlesMediaInfo: number,
  selectedVideoQuality: number,
  skipping: SkippingKind,
  subtitlesMediaInfo: Array<VideoPlayerMediaInfo>,
  timeshift: number,
  // Duration from location metadata
  totalDuration: number,
  updatingViewingHistory: boolean,
  userViewEndOffset: number,
  userViewStartOffset: number,
  volume: number,
};
